import { createElement } from 'react'
import { type RouteObject } from 'react-router'

import { type RegisteredApp } from '~/shared/appContract'
import { arcApp } from '~/apps/arc'
import { scoutApp } from '~/apps/scout'

import { addToAppNavCache } from './appNav'
import { withChunkLoadRecovery } from './chunkLoadRecovery'
import { RegisteredAppRoutes } from './RegisteredAppRoutes'

/**
 * Every app in the suite, in the order they are offered.
 *
 * This is the only place the shell learns that an app exists: the router is assembled from this list
 * rather than from named imports, so adding an app is an entry here plus its own directory. Each
 * entry is an app's own public entrypoint, so an app declares its own identity (id, display copy,
 * routes, and whether it is offered yet) and the shell only arranges what it is given (Atlas ADR-0017).
 *
 * Order is meaningful. After product permissions filter this list, the first remaining entry is the
 * deterministic fallback when neither the requested nor last-viewed app is available.
 */
const registeredApps = [arcApp, scoutApp]

export const appRegistry: RegisteredApp[] = registeredApps

/**
 * Every registered app's id, as a union of the ids themselves (`'arc' | 'scout'`).
 *
 * Derived from the registry rather than written out, so an app's id is declared once in the app's
 * own entrypoint.
 *
 * Use it wherever a value is *derived* from an app's id, so a typo is a compile error rather than a
 * lookup that silently matches nothing (see `productPermission` in `shell/auth/useEnabledProducts`).
 * It is not for identifying an app from outside input: `appById` still takes any `string`, because a
 * URL may name an app that does not exist.
 */
export type AppId = (typeof registeredApps)[number]['id']

/**
 * Every feature flag any registered app reads, as a union of the names themselves.
 *
 * Derived from each app's literal `flags` declaration (Atlas ADR-0028).
 */
export type AppFlagName = (typeof registeredApps)[number]['flags'][number]

/**
 * The apps a user may be sent to, in registry order.
 *
 * Every consumer that *offers* an app filters through this rather than reading `appRegistry`
 * directly, so a disabled app cannot be offered by one surface having forgotten to check the flag.
 * Mounting deliberately does not filter, so the router can recognize a disabled app and send it
 * through the same unavailable-app fallback as an unpermitted one.
 *
 * Reads the local list rather than `appRegistry` so each app's own id survives: a surface that offers
 * an app is exactly where an id turns into an entitlement lookup, and that lookup is typed on `AppId`.
 *
 * The typed predicate permits the optional `disabled` key without widening the returned ids and flags.
 */
const isOfferable = (app: RegisteredApp): boolean => !app.disabled

export const getEnabledApps = (): RegisteredApp<AppId>[] =>
  registeredApps.filter(isOfferable)

/**
 * The registered app with the given id, or `undefined` for an id no app claims.
 *
 * Reads the whole registry rather than `getEnabledApps`, because recognition and availability are
 * separate questions: a disabled app is recognized so its URL can produce an entitlement-driven
 * fallback rather than being classified as an unknown segment.
 */
export const appById = (appId: string | undefined): RegisteredApp | undefined =>
  appRegistry.find((app) => app.id === appId)

/**
 * Route metadata carrying the id of the app a route belongs to.
 *
 * Attached to each app's mount point below and read back with `useMatches` in `useActiveApp`, which
 * is how the shell knows the active app without an `:app` param.
 */
export interface AppRouteHandle {
  appId: string
}

/**
 * The registry's apps as routes to nest under the customer-scoped layout: one route per app.
 *
 * Each app is mounted under its own *literal* segment rather than a shared `:app` param. A single
 * param would collapse every app into one namespace, where React Router matches a child on its own
 * path alone and never checks that the child belongs to the app the URL names: one app's pages would
 * then resolve under another app's segment. A literal segment per app makes a cross-app path fail to
 * match at all, and keeps RUM view names attributed per app. See Atlas ADR-0014.
 *
 * Everything below that segment is the app's own, which is what the trailing splat says: the shell
 * matches which app a URL names and hands the rest to the app, whose own `<Routes>` resolves it or
 * redirects. The component behind the segment arrives through `lazy`, so an app's pages are a chunk
 * fetched on the first navigation into it, and a customer downloads no app but the one they use. The
 * router awaits that chunk before committing the navigation, so no app renders half-mounted; the
 * root's `HydrateFallback` covers the cold-deep-link case. See Atlas ADR-0019.
 *
 * The loader caches the app's nav alongside loading its routes, rather than handing `loadRoutes`
 * straight to the router, which is what lets the header render the nav without a Suspense boundary:
 * both come from one module per app, so the second call costs no request, and the router awaits this
 * before it commits the navigation that renders the header. See Atlas ADR-0021 for what suspending there
 * costs instead.
 *
 * `lazy` here is the *function* form, and must stay so. Its per-property object form is single-use:
 * resolving it sets the consumed key to `undefined`, and because a route's `lazy` object is shared
 * by reference with every router built from it, the second router would find a route with neither a
 * component nor a loader and report the URL as unmatched. Atlas ADR-0019 has the mechanism, and
 * `registry.test.tsx` pins it.
 */
export const mountedAppRoutes = (): RouteObject[] =>
  appRegistry.map((app) => ({
    path: `${app.id}/*`,
    handle: { appId: app.id } satisfies AppRouteHandle,
    lazy: async () => {
      const [routeModule] = await withChunkLoadRecovery(() =>
        Promise.all([app.loadRoutes(), addToAppNavCache(app)]),
      )

      const { Component, reducer } = routeModule
      if (reducer) {
        return {
          Component: () =>
            createElement(RegisteredAppRoutes, {
              appId: app.id,
              reducer,
              Component,
            }),
        }
      }

      return { Component }
    },
  }))
